import { Icon, Skeleton, Text } from '@dixa/lib-shared-habla';
import { typography } from '@dixa/lib-shared-tokens';
import { formatInstrumentNumber } from 'lib-analytics-ui';

import { AppLink } from 'components/appLink';

import styles from './dashboardLegend.module.scss';
import { DashboardLegendItem } from './dashboardLegend.types';

type DashboardLegendProps = {
  /** The legend items to show. */
  items: readonly DashboardLegendItem[];
  /** Whether to show the skeleton UI, e.g. when loading. */
  isSkeleton?: boolean;
  /** The theme. */
  theme?: 'light' | 'dark';
};

export const DashboardLegend = ({
  items,
  isSkeleton,
  theme = 'light',
}: DashboardLegendProps): JSX.Element => {
  const textColor = theme === 'dark' ? 'white' : undefined;

  return (
    <ul className={styles.root}>
      {items.map((item) => (
        <li key={item.title} className={styles.item}>
          {isSkeleton ? (
            <Skeleton height="10px" width="10px" borderRadius="50%" />
          ) : (
            <>
              {item.icon ? (
                <Icon
                  name={item.icon}
                  size={16}
                  color={theme === 'dark' ? 'white' : typography.color.tertiary}
                />
              ) : (
                <div
                  className={styles.icon}
                  style={{ backgroundColor: item.color }}
                />
              )}
            </>
          )}

          <div className={styles.title}>
            {isSkeleton ? (
              <Skeleton height="16px" width="64px" />
            ) : (
              <Text.Paragraph variant="4" color={textColor}>
                {item.linkProps ? (
                  <AppLink {...item.linkProps} className={styles.link}>
                    {item.title}
                  </AppLink>
                ) : (
                  item.title
                )}
              </Text.Paragraph>
            )}
          </div>

          {isSkeleton ? (
            <Skeleton height="16px" width="20px" />
          ) : (
            <Text.Paragraph variant="3" subdued color={textColor}>
              {item.type === 'number'
                ? formatInstrumentNumber(item.value, item.format)
                : item.value}
            </Text.Paragraph>
          )}
        </li>
      ))}
    </ul>
  );
};
